import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  memo,
  useEffect,
} from 'react';
import { MenuItem, Select, TextField } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StyledEditCellRenderer from '../../../../../../style/Common/Datagrid/EditCellRenderer/StyledEditCellRenderer';

/**
 * @description
 * Select modification cell renderer
 * @param {object} props
 * Grid props
 * @param {object} ref
 * Grid reference
 */
const GridSelectCell = forwardRef((props, ref) => {
  /** Get grid parameter */
  const {
    setValue,
    onValueChange,
    adornment,
    option: selectOption,
    onChangeHandler,
    isCancelBeforeStartHandler,
  } = props;

  /** Input inner component reference */
  const compRef = useRef(null);
  const compInputRef = useRef(null);

  /** Input component property */
  const [value, setDataValue] = useState(props?.value || '');
  const [option, setOption] = useState(selectOption || []);
  const [disabled, setDisabled] = useState(false);

  /** Component data setter */
  const compDataSetter = {
    setDataValue,
    setOption,
    setDisabled,
  };

  /**
   * Component Editor Lifecycle methods
   * @see https://www.ag-grid.com/react-data-grid/component-cell-editor/
   */
  useImperativeHandle(ref, () => {
    return {
      /** The final value to send to the grid, on completion of editing */
      getValue() {
        return value;
      },
      isCancelBeforeStart() {
        isCancelBeforeStartHandler?.({ ...props, ...compDataSetter });
        return false;
      },
      isCancelAfterEnd() {
        return false;
      },
      ref,
      compRef,
      compInputRef,
      ...compDataSetter,
      ...props,
    };
  });

  /** Render component */
  return (
    <StyledEditCellRenderer>
      <TextField
        select
        ref={compRef}
        inputRef={compInputRef}
        value={value || ''}
        onChange={e => {
          if (e) {
            const getChangedValue = e?.target?.value;
            setValue?.(getChangedValue);
            setDataValue?.(getChangedValue);
            onValueChange?.(getChangedValue);
            onChangeHandler?.({ getChangedValue, ...props });
          }
        }}
        MenuProps={{
          disableScrollLock: true,
          style: { zIndex: 9999 },
        }}
        SelectProps={{
          onClose: () => {
            props?.api?.setFocusedCell(
              props?.rowIndex,
              props?.column,
              props?.node?.rowPinned,
            );
            compInputRef?.current?.focus();
          },
        }}
        IconComponent={ExpandMoreIcon}
        disabled={disabled}
        /** Component Style */
        className={`border-solid border-[#D9D9D9] rounded-[5px] text-xs text-black w-[164px] h-[24px]  ${
          disabled ? `bg-[#F9F9F9]` : `bg-white`
        }`}
        sx={{
          // Select Arrow Icon
          fontWeight: '500',
          lineHeight: 'normal !important',
          '&.MuiTextField-root': {
            width: '100% !important',
          },
          '.MuiSvgIcon-root': {
            fontSize: '16px',
            color: '#264B9F !important',
          },
          '.MuiSelect-select': {
            height: 'unset !important',
            minHeight: 'unset !important',
            paddingLeft: '2px !important',
            paddingRight: '20px !important',
          },
          '.MuiInputBase-root': {
            lineHeight: 'normal !important',
            height: '100% !important',
            width: '100% !important',
            fontSize: '12px !important',
            fontWeight: '500 !important',
            '&:not(.Mui-focused):not(.Mui-disabled):hover fieldset': {
              borderColor: '#8E9396 !important',
            },
            '&.Mui-focused fieldset': {
              border: '1.2px solid #264B9F !important',
              boxShadow: '0px 0px 4px 0px #8BBCE9 !important',
            },
            '&.Mui-error fieldset': {
              border: '1.2px solid #C24D4D !important',
              backgroundColor: `#fff !important`,
            },
            '&.Mui-disabled fieldset': {
              backgroundColor: 'unset !important',
            },
          },
          fieldset: {
            borderColor: '#D9D9D9 !important',
          },
        }}
      >
        {option?.map((item, index) => {
          return (
            <MenuItem
              value={item?.value}
              key={String(index + 1)}
              className="text-xs"
              {...item}
            >
              {item?.label}
            </MenuItem>
          );
        })}
      </TextField>
    </StyledEditCellRenderer>
  );
});

export default GridSelectCell;
